import React from 'react';
import { FormInstance } from 'antd';
import juice from 'juice';
import EditorForm from './EditorForm';
import { INVITATION_STYLE } from 'utils/invitationStyle';
import { OpTypography } from 'components/customAntd/DLS/OpTypography/OpTypography';
import { OpColorPicker } from 'components/customAntd/DLS/OpColorPicker/OpColorPicker';
import { OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { OpSection } from 'components/customAntd/DLS/OpSection/OpSection';
import { OpButton } from 'components/customAntd/DLS/OpButton/OpButton';
import { OpModal } from 'components/customAntd/DLS/OpModal/OpModal';
import { useTranslation } from 'react-i18next';

const generateTemplateHtml = ({
  message,
  styleFrameColor,
}: {
  message: string;
  styleFrameColor: string;
  styleButtonColor: string;
  styleButtonTextColor: string;
}) => {
  return `
  <body style="margin: 0; padding: 0;">
    <table role = "presentation" cellpadding="0" align="center" width="820" style="background-color: ${styleFrameColor}; padding: 15px; border-radius: 16px; margin: 0 auto;">
      <tr>
        <td align="center">
          <table role="presentation" cellpadding="0" width="790" style="background-color: #ffffff; padding: 10px; border-radius: 16px; margin: 0 auto;">
            <tr>
              <td style="font-family: Arial, sans-serif; font-size: 16px; line-height: 1.4; color: #000000; word-break: break-word;">
                <style>
                  .message p, .message li, .message ul, .message ol {
                    font-size: 16px !important;
                    list-style-position: inside !important;
                    margin: 0 !important;
                    white-space: pre-wrap !important;
                  }
                  img {
                    max-width: 100%;
                    height: auto;
                    display: inline-block;
                  }
                </style>
                <div class="message">
                  ${message}
                <div>
              </td>
            </tr>
          </table>
        </td>
      </tr>
    </table>
  </body>
  `.trim();
};

export const getRawTemplateHTML = (form: FormInstance) => { //For API send
  const rawMessage = form.getFieldValue('content') || '';
  const styleFrameColor = form.getFieldValue('styleFrameColor');
  const styleButtonColor = form.getFieldValue('styleButtonColor');
  const styleButtonTextColor = form.getFieldValue('styleButtonTextColor');

  const html = generateTemplateHtml({ message: rawMessage, styleFrameColor, styleButtonColor, styleButtonTextColor });
  return juice(`<style>${INVITATION_STYLE}</style>${html}`);
};

interface FormatEmailFormProps {
  form: FormInstance;
  open: boolean;
  hasEmailTemplatesWrite: boolean;
}

const FormatEmailForm: React.FC<FormatEmailFormProps> = ({ form, open, hasEmailTemplatesWrite }) => {
  const [isPreviewModalOpen, setIsPreviewModalOpen] = React.useState(false);
  const { t } = useTranslation();

  const mergeFields = [
    { label: t('emailTemplates.form.mergeFields.startDate'), value: '[startdate]', key: 'startdate', sample: '2025-12-29' },
    { label: t('emailTemplates.form.mergeFields.endDate'), value: '[enddate]', key: 'enddate', sample: '2025-12-30' },
    { label: t('emailTemplates.form.mergeFields.startDateTime'), value: '[startdatetime]', key: 'startdatetime', sample: '2025-12-29 8:00 AM' },
    { label: t('emailTemplates.form.mergeFields.endDateTime'), value: '[enddatetime]', key: 'enddatetime', sample: '2025-12-30 5:30 PM' },
    { label: t('emailTemplates.form.mergeFields.organizationName'), value: '[organizationname]', key: 'organizationname', sample: 'Motorola Solutions Inc.' },
    { label: t('emailTemplates.form.mergeFields.visitorName'), value: '[visitorname]', key: 'visitorname', sample: 'John Doe' },
    { label: t('emailTemplates.form.mergeFields.hostName'), value: '[host]', key: 'host', sample: 'John Smith' },
    { label: t('emailTemplates.form.mergeFields.hostEmail'), value: '[hostemail]', key: 'hostemail', sample: 'john.smith@example.com' },
    { label: t('emailTemplates.form.mergeFields.purpose'), value: '[purpose]', key: 'purpose', sample: t('emailTemplates.form.mergeFields.purposeExample') },
    { label: t('emailTemplates.form.mergeFields.company'), value: '[company]', key: 'company', sample: 'InVisit' },
    { label: t('emailTemplates.form.mergeFields.locationName'), value: '[locationname]', key: 'locationname', sample: 'MSI HQ' },
    { label: t('emailTemplates.form.mergeFields.locationAddress'), value: '[locationaddress]', key: 'locationaddress', sample: '500 W Monroe St, Chicago, IL, 60661-3781, US' },
    { label: t('emailTemplates.form.mergeFields.preregistrationButton'), value: '[preregistration]', key: 'preregistration', sample: 'BUTTON GOES HERE' },
  ];

  const replaceMergeTags = (html: string, styleButtonColor: String, styleButtonTextColor: String) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    mergeFields.forEach(({ key, sample }) => {
      const regex = new RegExp(`\\[${key}\\]`, 'gi');
      let replacement = sample;

      if (key === 'preregistration') {
        replacement =
          `<a href="#"
              style="
                background-color: ${styleButtonColor};
                color: ${styleButtonTextColor};
                padding: 12px 12px;
                display: inline-block;
                border-radius: 6px;
                font-family: Arial, sans-serif;
                white-space: pre-wrap;
              ">${t('emailTemplates.form.mergeFields.preRegister')}</a>`
          ;
      }
      doc.body.innerHTML = doc.body.innerHTML.replace(regex, replacement);
    });

    return doc.body.innerHTML;
  };
  const getPreviewHTML = (form: FormInstance) => { //For Preview
    const rawMessage = form.getFieldValue('content') || '';
    const styleFrameColor = form.getFieldValue('styleFrameColor');
    const styleButtonColor = form.getFieldValue('styleButtonColor');
    const styleButtonTextColor = form.getFieldValue('styleButtonTextColor');

    const message = replaceMergeTags(rawMessage, styleButtonColor, styleButtonTextColor)
    const html = generateTemplateHtml({ message, styleFrameColor, styleButtonColor, styleButtonTextColor });
    return juice(`<style>${INVITATION_STYLE}</style>${html}`);
  };

  const renderColorPicker = (label: string, name: string) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <OpTypography.Text>{label}</OpTypography.Text>
      <OpForm.Item name={name} style={{ marginBottom: 0 }}>
        <OpColorPicker
          value={form.getFieldValue(name)}
          onChange={(color) => form.setFieldsValue({ [name]: color.toHexString() })}
          disabled={!hasEmailTemplatesWrite}
        />
      </OpForm.Item>
    </div>
  );

  const renderPreview = () => {
    const previewHtml = getPreviewHTML(form);
    return (
      <div data-preview-html style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
      </div>
    );
  }

  return (
    <>
      <OpForm.Item label={t('emailTemplates.form.style')} style={{ marginBottom: 0 }}>
        <OpSection>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {renderColorPicker(t('emailTemplates.form.frameColor'), 'styleFrameColor')}
            <div>
              {renderColorPicker(t('emailTemplates.form.buttonColor'), 'styleButtonColor')}
            </div>
            {renderColorPicker(t('emailTemplates.form.buttonTextColor'), 'styleButtonTextColor')}
          </div>
        </OpSection>
      </OpForm.Item>

      <OpForm.Item
        name="content"
        label={t('emailTemplates.form.body')}
        rules={[{
          required: true,
          validator: (_, val) => {
            const text = val?.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
            return text ? Promise.resolve() : Promise.reject(t('emailTemplates.form.fieldRequired'));
          }
        }]}
      >
        <EditorForm form={form} name="content" mergeFields={mergeFields} height={420} open={open} hasEmailTemplatesWrite={hasEmailTemplatesWrite} />
      </OpForm.Item>

      <div>
        <OpButton onClick={() => setIsPreviewModalOpen(true)}>{t('emailTemplates.form.preview')}</OpButton>
      </div>

      <OpModal
        title={t('emailTemplates.form.preview')}
        open={isPreviewModalOpen}
        onOk={() => setIsPreviewModalOpen(false)}
        onCancel={() => setIsPreviewModalOpen(false)}
        width={900}
      >
        {renderPreview()}
      </OpModal >

    </>
  );
};

export default FormatEmailForm;
